import React from "react";
import { useForm } from "react-hook-form";
import { Alert, Box, Grid, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { toast } from "react-toastify";

import MUIModal from "../../../common/MUIModal";
import { FormProvider, RHFTextField } from "../../../common/hook-form";
import { apiConstants, post } from "../../../common/constants";
import { getMerchant } from "../../../helpers/ReduxHelpers";

/**
 * AddApiUser
 * ----------
 * Creates an "API-only" merchant user. The user has ROLE_ADMIN and an empty
 * accessList on the backend, which means:
 *   - They can call any Proship API using their JWT (e.g. from Postman).
 *   - They cannot see any pages in the merchant panel UI (sidebar is empty).
 *
 * The initial password is auto-generated by the backend and emailed to the
 * user's email address (same flow as /auth/user_signup).
 */
const AddApiUser = ({ show, handleClose, loadUsers }) => {
  const [isLoading, setIsLoading] = React.useState(false);

  const merchant = getMerchant();
  const merchantId = merchant?.id || "";

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        username: Yup.string().email("Enter a valid email").required("Email is required"),
        fullname: Yup.string().required("Full name is required"),
        mobile: Yup.string()
          .matches(/^$|^[0-9]{10}$/, "Mobile must be empty or exactly 10 digits")
          .notRequired(),
      })
    ),
    defaultValues: {
      username: "",
      fullname: "",
      mobile: "",
    },
    shouldUnregister: true,
  });

  const { handleSubmit, reset } = methods;

  const hideModal = () => {
    reset();
    handleClose();
  };

  const onSubmitHandler = async (formData) => {
    try {
      setIsLoading(true);
      const payload = {
        username: formData.username,
        email: formData.username,
        fullname: formData.fullname,
        mobile: formData.mobile || undefined,
        merchantOid: merchantId,
      };

      await post(`${apiConstants.BASE_LOCAL}${apiConstants.CREATE_API_USER}`, payload);

      toast.success(
        "API user created. The initial password has been emailed to the user."
      );
      loadUsers();
      hideModal();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to create API user");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MUIModal
      open={show}
      handleClose={hideModal}
      title="Create API-Only User"
      maxWidth="md"
    >
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmitHandler)}>
        <Box sx={{ mb: 2 }}>
          <Alert severity="info">
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              What is an API-only user?
            </Typography>
            <Typography variant="body2">
              This user can call Proship APIs (e.g. from Postman or your
              backend integration) using its credentials, but{" "}
              <strong>cannot log in to the merchant panel UI</strong> &mdash;
              the sidebar will be empty.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              The initial password is auto-generated and will be emailed to the
              user's email address. Use it to obtain a JWT via{" "}
              <code>POST /api/auth/signin</code>.
            </Typography>
          </Alert>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="username"
              label="Email (used as username)"
              size="medium"
              variant="filled"
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="fullname"
              label="Full Name / Label"
              size="medium"
              variant="filled"
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="mobile"
              label="Mobile (10 digits, optional)"
              size="medium"
              variant="filled"
            />
          </Grid>
          <Grid item xs={12}>
            <LoadingButton
              loading={isLoading}
              type="submit"
              variant="contained"
              size="large"
              color="secondary"
            >
              Create API User
            </LoadingButton>
          </Grid>
        </Grid>
      </FormProvider>
    </MUIModal>
  );
};

export default AddApiUser;
